import * as React from 'react'

import Head from 'next/head'
import gql from 'graphql-tag'
import withHydrationOnDemand from 'react-hydration-on-demand'

import { ApolloClient, NormalizedCacheObject } from 'apollo-boost'
import { useLazyQuery } from '@apollo/react-hooks'

import {
  Country,
  Currency,
  Feature,
  FulfilmentMethod,
  Maybe,
  Page,
  PageAlternateLink,
  ProductList,
  ProductSort,
  QueryPageArgs,
  Widget,
} from '@thg-commerce/enterprise-network/src/generated/graphql'
import {
  FullHeaderLayout,
  i18n,
  LIST_EXTENSION,
  PageWrapper,
  Routes,
  useSessionSettings,
  useSiteConfig,
  useSiteDefinition,
  withCacheConfiguration,
} from '@thg-commerce/enterprise-core'
import { GridItem } from '@thg-commerce/gravity-system'
import {
  EnterpriseNextPage,
  EnterpriseNextPageContext,
  PageType,
} from '@thg-commerce/enterprise-core/src/App/types'
import { AllWidgetsFragment } from '@thg-commerce/enterprise-network/src/graphql/Fragment/Widgets/AllWidgets'
import { WidgetPictures } from '@thg-commerce/enterprise-network/src/graphql/Fragment/Widgets/WidgetPictures'
import {
  ComponentName,
  ComponentWidgetsVariables,
} from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Query/ComponentWidgets'

import { WidgetRenderer } from '@thg-commerce/enterprise-widgets'
import {
  DataLayerProducts,
  ProductListProductsArgs,
} from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Query/Page/ProductListProducts'
import { GravityPictureFragment } from '@thg-commerce/enterprise-network/src/graphql/Fragment/Picture.graphql'
import {
  FacetFields,
  ProductListFields,
} from '@thg-commerce/enterprise-network/src/graphql/Fragment/Content/ProductListFragments'
import { getFacetInputFromUrl } from '@thg-commerce/enterprise-widgets/src/ProductListWidgetRenderer/utils'
import {
  ChangeType,
  PageOptions,
  ProductItemProps,
  ProductListMarketedSpecialOfferProps,
  ProductListPage,
} from '@thg-commerce/enterprise-widget-product-list-page'
import { getFacetUrlFromInput } from '@thg-commerce/enterprise-widget-product-list-page/src/Facets/utils'
import { ProductCategoryItemProps } from '@thg-commerce/enterprise-widget-product-categories'
import { BreadcrumbsRenderer } from '@thg-commerce/enterprise-components'
import { spacing, styled } from '@thg-commerce/enterprise-theme'
import { pushToEnhancedDataLayer } from '@thg-commerce/enterprise-metrics/src/data_layer'
import { LandingPageContextProvider } from '@thg-commerce/enterprise-pages'
import { PageData } from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Query/Page/Page'

import { getPathAndParamsFromReq } from '../../src/utils'
import { checkForPrefixedPageMatch } from '../../src/helpers/page'
import CustomError from '../_error'

const GlobalWidgetRendererComponent = withHydrationOnDemand({
  on: [['delay', 500]],
})(WidgetRenderer)

export const PAGE_QUERY = gql`
  ${AllWidgetsFragment}
  ${GravityPictureFragment}
  ${WidgetPictures}

  query Page(
    $path: PagePath!
    $currency: Currency!
    $shippingDestination: Country!
  ) {
    page(
      path: $path
      currency: $currency
      shippingDestination: $shippingDestination
    ) @client {
      path
      title
      metaDescription
      canonicalUrl
      alternateLinks {
        locale
        pagePath
      }
      breadcrumbs {
        displayName
        pagePath
      }
      categories
      widgets @client {
        __typename

        ...AllWidgets

        ... on BrandsPageWidget {
          brands {
            char
            brands {
              name
              page {
                path
              }
              imageUrl
            }
          }
        }
        ... on GlobalTabbedWidgetSet {
          banners {
            ...AllWidgets
          }
        }

        ...WidgetPictures

        ... on ResponsiveProductTabs {
          banners {
            ...AllWidgets
            productList {
              products {
                ...ResponsiveProductBlockCarouselProductFields
                picture {
                  ...GravityPictureFragment
                }
                hoverPicture {
                  ...GravityPictureFragment
                }
                price {
                  price
                  rrp
                }
              }
            }
          }
        }
        ... on ResponsiveProductBlockCarousel {
          enableAsiaStyles
          title
          productList {
            products {
              ...ResponsiveProductBlockCarouselProductFields
              picture {
                ...GravityPictureFragment
              }
              price {
                price
                rrp
              }
            }
          }
        }
      }
    }
    componentWidgets(
      name: $name
      currency: $currency
      shippingDestination: $shippingDestination
    ) @client {
      ...AllWidgets
      ...WidgetPictures
    }

    productListProducts(
      path: $path
      input: $input
      currency: $currency
      shippingDestination: $shippingDestination
      clickAndCollectEnabled: $clickAndCollectEnabled
      wishlistEnabled: $wishlistEnabled
      strict: false
    ) @client {
      page(path: $path) {
        widgets {
          id
          __typename
          ... on ProductListWidget {
            title
            description
            seoDescription
            dataLayerProducts {
              name
              price
              id
              brand
              list_position
              list_name
            }
            products: productList(input: $input) {
              __typename
              total
              facets {
                __typename
                ...FacetFields
              }
              products {
                __typename
                ...ProductListFields
                price {
                  price
                  rrp
                }
                colourSwatches
              }
            }
          }
        }
      }
    }
  }
  ${FacetFields}
  ${ProductListFields}
`

const CONCESSION_CODE_QUERY = gql`
  query ConcessionPage($path: PagePath!, $concessionEnabled: Boolean! = false) {
    page(path: $path, concessionEnabled: $concessionEnabled) @client {
      concession @include(if: $concessionEnabled) {
        concessionCode
        footer {
          navigation {
            topLevel {
              link {
                url
              }
            }
          }
        }
        header {
          navigation {
            topLevel {
              link {
                url
              }
            }
          }
        }
      }
    }
  }
`

export interface LandingPageProps {
  path: string
  page?: PageData
  componentWidgets?: Maybe<Widget[]>
  productList?: any
  dataLayerProducts?: DataLayerProducts
  pageParams: {
    pageNumber?: string
    facetFilters?: string
    sortOrder?: string
  }
  statusCode?: number
  showBackwardNavigationForBreadcrumbs?: boolean
}

const productProcessing = (product) => {
  const marketedSpecialOffer:
    | ProductListMarketedSpecialOfferProps
    | undefined = product.marketedSpecialOffer
    ? {
        title: product.marketedSpecialOffer.title?.content?.[0]?.content ?? '',
        description:
          product.marketedSpecialOffer.description?.content?.[0]?.content,
        freeGiftProduct: product.marketedSpecialOffer.freeGiftProduct
          ? {
              images: [
                {
                  largeProduct:
                    product.marketedSpecialOffer.freeGiftProduct.images?.[0]
                      ?.largeProduct ?? '',
                },
              ],
              title: product.marketedSpecialOffer.freeGiftProduct.title,
              price: {
                rrp: {
                  displayValue:
                    product.marketedSpecialOffer.freeGiftProduct.price?.rrp
                      .displayValue ?? '',
                },
              },
            }
          : undefined,
        landingPageLink: product.marketedSpecialOffer.landingPageLink
          ? {
              text: product.marketedSpecialOffer.landingPageLink.text,
              url: product.marketedSpecialOffer.landingPageLink.url,
              openExternally: !!product.marketedSpecialOffer.landingPageLink
                .openExternally,
              noFollow: !!product.marketedSpecialOffer.landingPageLink.noFollow,
            }
          : undefined,
      }
    : undefined

  const fulfilmentMethods = product.defaultVariant?.eligibleForFulfilmentMethods
  return {
    ...product,
    marketedSpecialOffer,
    inStock: product.variants.find((variant) => variant.inStock) !== undefined,
    icons: fulfilmentMethods
      ? {
          deliveryAvailable: fulfilmentMethods.includes(
            FulfilmentMethod.HomeDelivery,
          ),
          storeAvailable: fulfilmentMethods.includes(
            FulfilmentMethod.CollectInStore,
          ),
        }
      : undefined,
    reviews: product.reviews
      ? {
          total: product.reviews.total,
          averageScore: product.reviews.averageScore,
        }
      : undefined,
    colourSwatches: product.colourSwatches,
    wishlist: {
      wishlistEnabled: true,
      inWishlist: product.inWishlist || false,
    },
  }
}

export const BreadcrumbsRendererWrapper = styled(BreadcrumbsRenderer)`
  max-width: ${(props) => props.theme.site.siteWidth};
  padding-bottom: ${spacing(2)};
`

const Landing: EnterpriseNextPage<LandingPageProps> = (
  props: LandingPageProps,
) => {
  const [productList, setProductList] = React.useState(props.productList)

  const i18nText = {
    metaTagKeywords: i18n('meta.keywords.end.text'),
    siteTitleEnd: i18n('titles.end.text'),
    defaultMetaDescriptionPart1: i18n('meta.description.list.default.1'),
    defaultMetaDescriptionPart2: i18n('meta.description.list.default.2'),
    defaultMetaDescriptionPart3: i18n('meta.description.list.default.3'),
  }
  const { siteName, originUrl, defaultSessionSettings } = useSiteDefinition()
  const { enhancedEcommerceEnabled } = useSiteConfig()
  const sessionSettings = useSessionSettings()

  const [getPage, { loading, error, data }] = useLazyQuery<
    {
      page: Page
      componentWidgets: Maybe<Widget[]>
      productListProducts?: {
        description: string | null
        seoDescription: string | null
        page: Page
      }
    },
    QueryPageArgs & ComponentWidgetsVariables & ProductListProductsArgs
  >(PAGE_QUERY)

  React.useEffect(() => {
    if (data) {
      const productList = (data?.productListProducts?.page?.widgets
        ?.filter((widget) => widget['__typename'] === 'ProductListWidget')
        .pop() as unknown) as {
        description: string | null
        seoDescription: string | null
        products: ProductList
      }

      // TODO: move to ProductListProducts resolver
      const products: ProductItemProps[] | undefined | null =
        productList?.products &&
        productList?.products.products.map((product) =>
          productProcessing(product),
        )

      const topProductCategorySet = data.page?.widgets?.find(
        (item) => item['__typename'] === 'TopProductCategorySet',
      ) as any

      const productCategoryItems: ProductCategoryItemProps[] =
        topProductCategorySet?.banners
          ?.filter(
            (item) => item.topProductCategoryName && item.topProductCategoryUrl,
          )
          .map((item) => {
            return {
              text: item.topProductCategoryName as string,
              href: item.topProductCategoryUrl as string,
            }
          }) || []

      setProductList({
        // TODO: conditionally merge when products exist
        itemsPerPage: props.productList?.itemsPerPage,
        title: props.productList?.title || '',
        description: productList.description || '',
        seoDescription: productList.seoDescription || '',
        changeType: ChangeType.NONE,
        categories: productCategoryItems,
        categoriesTitle: topProductCategorySet?.DiscoverTitle,
        facets: productList?.products?.facets,
        pageOptions: {
          sort: props.productList?.pageOptions?.sort || ProductSort.Relevance,
          facets: getFacetInputFromUrl(
            new URL(window.location.href).searchParams.get('facetFilters') ||
              '',
          ),
          pageNumber: props.pageParams.pageNumber
            ? parseInt(props.pageParams.pageNumber, 10)
            : 1,
        },
        totalItems: productList?.products?.total || products.length || 0,
        products: products || [],
      })
    }
  }, [loading, data, error])

  React.useEffect(() => {
    if (enhancedEcommerceEnabled && props.productList) {
      pushToEnhancedDataLayer({
        event: 'view_item_list',
        ecommerce: {
          list: {
            products: props.dataLayerProducts,
          },
        },
      })
    }
  }, [])

  if (!props.page || props.statusCode === 404) {
    return <CustomError statusCode={props.statusCode || 404} />
  }

  const breadcrumbs = props.page.breadcrumbs.map((item) => {
    return {
      text: item.displayName,
      url:
        item.pagePath === '/'
          ? item.pagePath
          : item.pagePath.concat(LIST_EXTENSION),
    }
  })

  const breadcrumbSchemaItems = breadcrumbs.map((breadcrumb, index) => {
    return {
      '@type': 'ListItem',
      position: index,
      name: breadcrumb.text,
      item: originUrl.concat(breadcrumb.url),
    }
  })

  return (
    <React.Fragment>
      <Head>
        <title>{props?.page?.title || i18nText.siteTitleEnd}</title>
        <meta
          name="description"
          content={
            props?.page?.metaDescription ||
            `${i18nText.defaultMetaDescriptionPart1} ${props?.page?.title} ${i18nText.defaultMetaDescriptionPart2} ${i18nText.defaultMetaDescriptionPart3}`
          }
        />
        <meta
          name="keywords"
          content={`${siteName}, ${i18nText.metaTagKeywords}`}
        />
        {props?.page?.alternateLinks.map((link: PageAlternateLink) => (
          <link
            key={link.locale}
            rel="alternative"
            href={`${originUrl}${link.pagePath}${
              link.pagePath !== '/' ? LIST_EXTENSION : ''
            }`}
            hrefLang={link.locale}
          />
        ))}
        {props?.page?.canonicalUrl && (
          <link
            rel="canonical"
            href={`${originUrl}${props.page.canonicalUrl}`}
            key="link_canonical"
          />
        )}
      </Head>
      <LandingPageContextProvider categories={props.page.categories}>
        <PageWrapper enableMaxWidth={false}>
          <GridItem colSpan={12}>
            <BreadcrumbsRendererWrapper
              breadcrumbs={breadcrumbs}
              schemaItems={breadcrumbSchemaItems}
              enableBackButton={props.showBackwardNavigationForBreadcrumbs}
            />
            <WidgetRenderer
              widgets={props.page.widgets || []}
              path={props.path}
              pageParams={props.pageParams}
            />
            {props.productList && (
              <ProductListPage
                {...productList}
                loading={loading}
                sortTypes={[
                  {
                    type: ProductSort.Relevance,
                    displayText: i18n('product.list.sort.relevance'),
                  },
                  {
                    type: ProductSort.Popularity,
                    displayText: i18n('product.list.sort.popularity'),
                  },
                  {
                    type: ProductSort.PriceLowToHigh,
                    displayText: i18n('product.list.sort.pricelowtohigh'),
                  },
                  {
                    type: ProductSort.PriceHighToLow,
                    displayText: i18n('product.list.sort.pricehightolow'),
                  },
                  {
                    type: ProductSort.NameAlphabetically,
                    displayText: i18n('product.list.sort.namealphabetically'),
                  },
                  {
                    type: ProductSort.NewestToOldest,
                    displayText: i18n('product.list.sort.newesttooldest'),
                  },
                  {
                    type: ProductSort.DiscountPercentageHighToLow,
                    displayText: i18n(
                      'product.list.sort.discountpercentagehightolow',
                    ),
                  },
                ]}
                onInputChange={(newInput: PageOptions) => {
                  const url = new URL(window.location.href)
                  url.hash = 'ProductList'
                  if (newInput.pageNumber) {
                    newInput.pageNumber > 1
                      ? url.searchParams.set(
                          'pageNumber',
                          newInput.pageNumber.toString(),
                        )
                      : url.searchParams.delete('pageNumber')
                  }
                  if (newInput.sort) {
                    url.searchParams.set('sortOrder', newInput.sort.toString())
                    url.searchParams.delete('pageNumber')
                  }

                  if (newInput.facets) {
                    url.searchParams.set(
                      'facetFilters',
                      getFacetUrlFromInput(newInput.facets),
                    )
                    url.searchParams.delete('pageNumber')
                    window.history.replaceState(
                      {},
                      document.title,
                      url.toString(),
                    )
                    getPage({
                      variables: {
                        path: props.path,
                        name: ComponentName.GLOBAL,
                        wishlistEnabled: true,
                        clickAndCollectEnabled: false,
                        input: {
                          currency:
                            sessionSettings?.currency ||
                            defaultSessionSettings.currency,
                          shippingDestination:
                            sessionSettings?.shippingDestination ||
                            defaultSessionSettings.shippingDestination,
                          limit: props.productList?.itemsPerPage,
                          offset: 0, // reset offset as we're updating facets
                          sort:
                            (url.searchParams.get(
                              'sortOrder',
                            ) as ProductSort) || ProductSort.Relevance,
                          facets: getFacetInputFromUrl(
                            url.searchParams.get('facetFilters') || '',
                          ),
                        },
                        currency:
                          sessionSettings?.currency ||
                          defaultSessionSettings.currency,
                        shippingDestination:
                          sessionSettings?.shippingDestination ||
                          defaultSessionSettings.shippingDestination,
                      },
                    })
                  } else {
                    window.location.href = url.toString()
                  }
                }}
              />
            )}
          </GridItem>
        </PageWrapper>
        <GlobalWidgetRendererComponent
          widgets={props.componentWidgets || []}
          path={props.path}
          pageParams={props.pageParams}
        />
      </LandingPageContextProvider>
    </React.Fragment>
  )
}

Landing.Layout = FullHeaderLayout

Landing.supportsConcessions = true

Landing.pageType = (route: string) => {
  if (route === Routes.HomePage) {
    return PageType.HOME_PAGE
  }

  return PageType.LANDING
}

Landing.getConcessionCode = async (
  ctx: EnterpriseNextPageContext,
  apolloClient?: ApolloClient<NormalizedCacheObject>,
) => {
  const horizonFeatures = ctx.req.horizonFeatures || []

  const concessionEnabled = horizonFeatures?.includes(Feature.Concessions)

  if (!concessionEnabled) {
    return undefined
  }

  const { path } = getPathAndParamsFromReq(ctx)

  if (!path || path === '') {
    return undefined
  }

  try {
    if (apolloClient) {
      const { data } = await apolloClient.query<{ page: Page }>({
        query: CONCESSION_CODE_QUERY,
        variables: { path, concessionEnabled },
      })
      return data?.page?.concession?.concessionCode
    }
    return undefined
  } catch (error) {
    console.error('Unable to fetch concession code from horizon', error)
    return undefined
  }
}

Landing.getInitialProps = withCacheConfiguration<
  LandingPageProps,
  EnterpriseNextPageContext
>(async (ctx) => {
  const results = getPathAndParamsFromReq(ctx)

  const {
    siteConfig,
    siteDefinition,
    shippingDestination,
  } = ctx.config.publicRuntimeConfig

  const {
    productItemsPerPage,
    showBackwardNavigationForBreadcrumbs,
  } = siteConfig
  const { sessionSettings } = ctx.req.config

  const itemsPerPage = productItemsPerPage || 45
  const selectedPageNumber =
    results.pageParams?.pageNumber &&
    parseInt(results.pageParams?.pageNumber, 10) >= 1
      ? parseInt(results.pageParams?.pageNumber, 10)
      : 1
  const sortOrder =
    (results.pageParams?.sortOrder as ProductSort) || ProductSort.Relevance

  const path =
    results.path === ''
      ? '/'
      : new URL(
          decodeURIComponent(results.path),
          siteDefinition.originUrl,
        ).pathname.replace(/\./g, '') // @TODO: Improve handling of URL's that contain dots - Horizon throws an exception

  try {
    const { data } = await ctx.apolloClient.query<
      {
        page: PageData
        componentWidgets: Maybe<Widget[]>
        productListProducts?: { page: Page }
      },
      QueryPageArgs & ComponentWidgetsVariables & ProductListProductsArgs
    >({
      query: PAGE_QUERY,
      variables: {
        path,
        name: ComponentName.GLOBAL,
        wishlistEnabled: true,
        clickAndCollectEnabled: false,
        input: {
          currency:
            sessionSettings?.currency ||
            (siteDefinition.defaultCurrency as Currency),
          shippingDestination:
            sessionSettings?.shippingDestination ||
            (shippingDestination.code as Country),
          limit: itemsPerPage,
          offset:
            selectedPageNumber > 1
              ? itemsPerPage * (selectedPageNumber - 1)
              : 0,
          sort: sortOrder,
          facets: getFacetInputFromUrl(
            results.pageParams.facetFilters
              ? decodeURIComponent(results.pageParams.facetFilters)
              : '',
          ),
        },
        currency:
          sessionSettings?.currency ||
          (siteDefinition.defaultCurrency as Currency),
        shippingDestination:
          sessionSettings?.shippingDestination ||
          (shippingDestination.code as Country),
      },
      context: {
        pagePath: path,
        currency:
          sessionSettings?.currency ||
          (siteDefinition.defaultCurrency as Currency),
        shippingDestination:
          sessionSettings?.shippingDestination ||
          (shippingDestination.code as Country),
      },
    })

    const widgets = data?.page?.widgets?.filter(
      (widget) => widget['__typename'] !== 'ProductListWidget',
    )

    const productList = (data?.productListProducts?.page?.widgets
      ?.filter((widget) => widget['__typename'] === 'ProductListWidget')
      .pop() as unknown) as {
      description: string | null
      seoDescription: string | null
      title: string | null
      products: ProductList
      dataLayerProducts: DataLayerProducts
    }

    const topProductCategorySet = data?.page?.widgets?.find(
      (item) => item['__typename'] === 'TopProductCategorySet',
    ) as any

    // TODO: move to ProductListProducts resolver
    const products: ProductItemProps[] | undefined | null =
      productList?.products &&
      productList?.products.products.map((product) =>
        productProcessing(product),
      )

    const productCategoryItems: ProductCategoryItemProps[] =
      topProductCategorySet?.banners
        ?.filter(
          (item) => item.topProductCategoryName && item.topProductCategoryUrl,
        )
        .map((item) => {
          return {
            text: item.topProductCategoryName as string,
            href: item.topProductCategoryUrl as string,
          }
        }) || []

    if (data?.page) {
      return productList
        ? {
            ...results,
            path,
            showBackwardNavigationForBreadcrumbs,
            componentWidgets: data.componentWidgets,
            cache: {
              ttl: 300,
              grace: 3600,
            },
            page: { ...data.page, widgets },
            dataLayerProducts: productList.dataLayerProducts,
            productList: {
              // TODO: conditionally merge when products exist
              itemsPerPage,
              title: productList?.title || '',
              description: productList?.description || '',
              seoDescription: productList?.seoDescription || '',
              changeType: ChangeType.NONE,
              categories: productCategoryItems,
              categoriesTitle: topProductCategorySet?.DiscoverTitle,
              facets: productList?.products?.facets,
              pageOptions: {
                sort: sortOrder,
                facets: getFacetInputFromUrl(
                  results.pageParams.facetFilters
                    ? decodeURIComponent(results.pageParams.facetFilters)
                    : '',
                ),
                pageNumber: results.pageParams.pageNumber
                  ? parseInt(results.pageParams.pageNumber, 10)
                  : 1,
              },
              totalItems: productList?.products?.total || products?.length || 0,
              products: products || [],
            },
          }
        : {
            ...results,
            path,
            showBackwardNavigationForBreadcrumbs,
            componentWidgets: data.componentWidgets,
            cache: {
              ttl: 300,
              grace: 3600,
            },
            page: { ...data.page, widgets },
          }
    }
  } catch (error) {
    ctx.logger.warn(`Failed to load list page with error ${error.message}`, {
      stack: error.stack,
    })
  }

  if (ctx.res) {
    const prefixedPageMatch = await checkForPrefixedPageMatch(
      path,
      ctx.apolloClient,
    )
    if (prefixedPageMatch) {
      const redirectURL = new URL(
        `${prefixedPageMatch}.list`,
        `https://${siteDefinition.domain}`,
      )
      Object.entries(ctx.req.query).forEach(([key, value]) => {
        if (typeof value === 'string') {
          redirectURL.searchParams.set(key, value)
        }
      })

      ctx.res.redirect(301, redirectURL.toString())
    } else {
      ctx.res.statusCode = 404
    }
  }

  return {
    path,
    page: undefined,
    componentWidgets: [],
    productList: undefined,
    pageParams: {
      sortOrder,
      pageNumber: undefined,
      facetFilters: undefined,
    },
    statusCode: ctx.res.statusCode || 404,
  }
})

export default Landing
